/** CharacterStore — 絵筆キャラクター(プレイヤービジュアル)の選択 + アンロック状態を永続化。
 *  Reference: docs/CLAUDE.md §3.5 Living World(コスメ通貨経路の起点)
 *  - 性能差なし(Painterly Joy + Skill Honored 死守)
 *  - アンロック条件:ステージ進行 / Color 購入 / 広告視聴(Phase A デモ用)
 *
 *  Callers:
 *    - web/src/main.ts: 生成 + unlockByStageClear(stageId)
 *    - web/src/ui/screens/CharacterScreen.ts: open() / close()
 *    - web/src/ui/screens/GameplayScreen.ts: getCharacterMeta callback → Player に渡す
 */

export type CharacterId =
  | 'apprentice'   // 初期解放(ゴールド+クリーム)
  | 'sakura'       // ピンク + ゴールド、桜の絵筆
  | 'azure'        // 群青 + 銀、深海の絵筆
  | 'ember'        // 朱 + 黒墨、炎の絵筆
  | 'forest'       // 翠 + 土、森の絵筆
  | 'aurora';      // 虹グラデ + 銀、極北の絵筆

export interface CharacterMeta {
  readonly id: CharacterId;
  readonly name: string;        // ja: 表示名
  readonly description: string; // ja: 短い説明
  readonly bodyColor: number;   // 0xRRGGBB, ドラゴン胴体色
  readonly accentColor: number; // ゴールド/銀などのアクセント
  readonly inkColor: number;    // 縁取り
  readonly creamColor: number;  // ハイライト
  readonly handleColor: number; // 絵筆の柄
  readonly unlockCondition: {
    readonly type: 'default' | 'stage_clear' | 'color' | 'reward_ad';
    readonly stageId?: string;
    readonly colorPrice?: number;
  };
}

export const CHARACTER_META: Record<CharacterId, CharacterMeta> = {
  apprentice: {
    id: 'apprentice',
    name: '見習いの絵筆',
    description: '工房の最初の友。',
    bodyColor: 0xffcf54,
    accentColor: 0xffcf54,
    inkColor: 0x39210f,
    creamColor: 0xfff1cc,
    handleColor: 0x8a552b,
    unlockCondition: { type: 'default' },
  },
  sakura: {
    id: 'sakura',
    name: '桜の絵筆',
    description: '春のひと刷毛。',
    bodyColor: 0xf5a8c8,
    accentColor: 0xffcf54,
    inkColor: 0x4a2540,
    creamColor: 0xffe4ed,
    handleColor: 0x8a3c5e,
    unlockCondition: { type: 'stage_clear', stageId: '1-05' },
  },
  azure: {
    id: 'azure',
    name: '群青の絵筆',
    description: '聖母の衣に用いられた色。',
    bodyColor: 0x3a6db8,
    accentColor: 0xd0d8e8,
    inkColor: 0x1a2840,
    creamColor: 0xe0e8f4,
    handleColor: 0x2a4878,
    unlockCondition: { type: 'stage_clear', stageId: '1-10' },
  },
  ember: {
    id: 'ember',
    name: '炎の絵筆',
    description: '炎を映す朱と墨。',
    bodyColor: 0xd13c2a,
    accentColor: 0xffcf54,
    inkColor: 0x2a0a0a,
    creamColor: 0xffd4b0,
    handleColor: 0x6a1818,
    unlockCondition: { type: 'color', colorPrice: 1500 },
  },
  forest: {
    id: 'forest',
    name: '森の絵筆',
    description: '苔と樹皮の囁き。',
    bodyColor: 0x5c8a3a,
    accentColor: 0xc9a86a,
    inkColor: 0x1a2a0a,
    creamColor: 0xe4ecc0,
    handleColor: 0x4a3a1a,
    unlockCondition: { type: 'reward_ad' },
  },
  aurora: {
    id: 'aurora',
    name: '極北の絵筆',
    description: '夜空にゆらぐ虹の絵具。',
    bodyColor: 0x9c7adb,
    accentColor: 0xfff5cf,
    inkColor: 0x2a1840,
    creamColor: 0xf4e8ff,
    handleColor: 0x4a2a78,
    unlockCondition: { type: 'color', colorPrice: 3000 },
  },
};

export const CHARACTER_IDS: readonly CharacterId[] = [
  'apprentice',
  'sakura',
  'azure',
  'ember',
  'forest',
  'aurora',
] as const;

const STORAGE_SELECTED = 'ck.selectedCharacter';
const STORAGE_UNLOCKED = 'ck.unlockedCharacters';

const VALID_IDS = new Set<string>(CHARACTER_IDS);

function isCharacterId(v: unknown): v is CharacterId {
  return typeof v === 'string' && VALID_IDS.has(v);
}

export class CharacterStore {
  private selectedId: CharacterId;
  private unlocked: Set<CharacterId>;

  constructor() {
    this.unlocked = this.loadUnlocked();
    // apprentice は常に解放(初期保証)
    this.unlocked.add('apprentice');
    this.saveUnlocked();
    this.selectedId = this.loadSelected();
  }

  private loadSelected(): CharacterId {
    try {
      const raw = window.localStorage.getItem(STORAGE_SELECTED);
      if (raw && isCharacterId(raw) && this.unlocked.has(raw)) return raw;
    } catch {
      /* noop(プライベートモード等) */
    }
    return 'apprentice';
  }

  private loadUnlocked(): Set<CharacterId> {
    try {
      const raw = window.localStorage.getItem(STORAGE_UNLOCKED);
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const ids = parsed.filter(isCharacterId);
          return new Set(ids);
        }
      }
    } catch {
      /* noop */
    }
    return new Set<CharacterId>();
  }

  private saveSelected(): void {
    try {
      window.localStorage.setItem(STORAGE_SELECTED, this.selectedId);
    } catch {
      /* noop */
    }
  }

  private saveUnlocked(): void {
    try {
      window.localStorage.setItem(
        STORAGE_UNLOCKED,
        JSON.stringify(Array.from(this.unlocked)),
      );
    } catch {
      /* noop */
    }
  }

  getSelected(): CharacterId {
    return this.selectedId;
  }

  getSelectedMeta(): CharacterMeta {
    return CHARACTER_META[this.selectedId];
  }

  isUnlocked(id: CharacterId): boolean {
    return this.unlocked.has(id);
  }

  isSelected(id: CharacterId): boolean {
    return this.selectedId === id;
  }

  /** 選択を変更(未解放なら no-op + false 返却) */
  select(id: CharacterId): boolean {
    if (!this.unlocked.has(id)) return false;
    this.selectedId = id;
    this.saveSelected();
    return true;
  }

  /** ステージクリア時に呼ぶ:該当 stageId の condition を満たすキャラを自動解放 */
  unlockByStageClear(stageId: string): CharacterId[] {
    const newly: CharacterId[] = [];
    for (const id of CHARACTER_IDS) {
      const cond = CHARACTER_META[id].unlockCondition;
      if (
        cond.type === 'stage_clear' &&
        cond.stageId === stageId &&
        !this.unlocked.has(id)
      ) {
        this.unlocked.add(id);
        newly.push(id);
      }
    }
    if (newly.length > 0) this.saveUnlocked();
    return newly;
  }

  /** Color 購入による解放 */
  unlockByColor(id: CharacterId): boolean {
    if (this.unlocked.has(id)) return false;
    const cond = CHARACTER_META[id].unlockCondition;
    if (cond.type !== 'color') return false;
    this.unlocked.add(id);
    this.saveUnlocked();
    return true;
  }

  /** 広告報酬による解放 */
  unlockByAd(id: CharacterId): boolean {
    if (this.unlocked.has(id)) return false;
    const cond = CHARACTER_META[id].unlockCondition;
    if (cond.type !== 'reward_ad') return false;
    this.unlocked.add(id);
    this.saveUnlocked();
    return true;
  }
}
